import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Calendar } from 'lucide-react';
import BookingDatePicker from './BookingDatePicker';

const DIRECT_BOOKING_URL = process.env.REACT_APP_BOOKING_URL;

export function SiteHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const bookingUrl = `${DIRECT_BOOKING_URL}?locale=en&currency=GBP&trackPage=no`;

  return (
    <header className="fixed top-0 w-full z-50 bg-[#0F1419]/95 backdrop-blur-xl border-b border-[#C9A961]/20">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-4 lg:py-5 flex items-center justify-between">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <Link to="/" className="flex items-center space-x-2 sm:space-x-3">
            <div className="border-2 border-[#C9A961] p-2 sm:p-3 bg-[#0F1419]">
              <div className="text-xl sm:text-2xl font-serif text-[#C9A961] leading-none tracking-tighter">W</div>
            </div>
            <div>
              <div className="text-base sm:text-xl font-serif text-[#C9A961] tracking-tight leading-tight">Wentworth House</div>
              <div className="text-[8px] sm:text-[9px] tracking-[0.3em] text-[#C9A961]/60 uppercase">Bath &middot; Est. 2003</div>
            </div>
          </Link>
        </div>

        <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
          <Link to="/" className="text-base font-light text-[#C9A961]/70 hover:text-[#C9A961] border-b-2 border-transparent hover:border-[#C9A961] pb-1 transition-all duration-300" data-testid="nav-home">Home</Link>
          <Link to="/rooms" className="text-base font-light text-[#C9A961]/70 hover:text-[#C9A961] border-b-2 border-transparent hover:border-[#C9A961] pb-1 transition-all duration-300" data-testid="nav-rooms">Rooms</Link>
          <Link to="/discover-bath" className="text-base font-light text-[#C9A961]/70 hover:text-[#C9A961] border-b-2 border-transparent hover:border-[#C9A961] pb-1 transition-all duration-300" data-testid="nav-discover">Discover Bath</Link>
          <Link to="/offers" className="text-base font-light text-[#C9A961]/70 hover:text-[#C9A961] border-b-2 border-transparent hover:border-[#C9A961] pb-1 transition-all duration-300" data-testid="nav-offers">Offers</Link>
          <Link to="/contact" className="text-base font-light text-[#C9A961]/70 hover:text-[#C9A961] border-b-2 border-transparent hover:border-[#C9A961] pb-1 transition-all duration-300" data-testid="nav-contact">Contact</Link>
        </div>

        <div className="flex items-center space-x-3">
          <a href={bookingUrl} target="_blank" rel="noopener noreferrer external" className="hidden sm:inline-flex bg-[#C9A961] text-[#0F1419] px-6 lg:px-8 py-3 text-xs sm:text-sm tracking-[0.15em] uppercase font-bold transition-all duration-300 hover:bg-[#D4B76E] border border-[#C9A961]" data-testid="header-book-btn">Book Now</a>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 text-[#C9A961]" data-testid="mobile-menu-toggle">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#0F1419] border-t border-[#C9A961]/20 px-4 pb-6 animate-slideDown" data-testid="mobile-menu">
          <div className="flex flex-col space-y-1 pt-4">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="text-left text-lg py-3 px-4 text-[#C9A961]/80 hover:text-[#C9A961] hover:bg-[#C9A961]/10 transition-all duration-200">Home</Link>
            <Link to="/rooms" onClick={() => setMobileMenuOpen(false)} className="text-left text-lg py-3 px-4 text-[#C9A961]/80 hover:text-[#C9A961] hover:bg-[#C9A961]/10 transition-all duration-200">Rooms</Link>
            <Link to="/discover-bath" onClick={() => setMobileMenuOpen(false)} className="text-left text-lg py-3 px-4 text-[#C9A961]/80 hover:text-[#C9A961] hover:bg-[#C9A961]/10 transition-all duration-200">Discover Bath</Link>
            <Link to="/offers" onClick={() => setMobileMenuOpen(false)} className="text-left text-lg py-3 px-4 text-[#C9A961]/80 hover:text-[#C9A961] hover:bg-[#C9A961]/10 transition-all duration-200">Offers</Link>
            <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="text-left text-lg py-3 px-4 text-[#C9A961]/80 hover:text-[#C9A961] hover:bg-[#C9A961]/10 transition-all duration-200">Contact</Link>
            <a href={bookingUrl} target="_blank" rel="noopener noreferrer external" onClick={() => setMobileMenuOpen(false)} className="mt-3 block w-full text-center bg-[#C9A961] text-[#0F1419] py-4 text-sm tracking-[0.15em] uppercase font-bold">Book Now</a>
          </div>
        </div>
      )}
    </header>
  );
}

export function SiteFooter({ settings = {} }) {
  return (
    <footer className="bg-[#0a0d12] border-t border-[#C9A961]/15 pt-12 sm:pt-16 pb-6 px-4 sm:px-6 lg:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 sm:gap-10 mb-10 sm:mb-14">
          <div>
            <div className="flex items-center space-x-2 mb-5">
              <div className="border border-[#C9A961] p-1.5"><div className="text-sm font-serif text-[#C9A961]">W</div></div>
              <div className="text-xs font-serif text-[#C9A961]">Wentworth House</div>
            </div>
            <p className="text-xs text-[#E8E4DC]/40 leading-relaxed">A Georgian guest house in Bath, dating back to 1809.</p>
          </div>
          <div>
            <h4 className="text-xs tracking-[0.15em] uppercase text-[#C9A961] mb-4 font-semibold">Quick Links</h4>
            <div className="space-y-2">
              <Link to="/about" className="block text-sm text-[#E8E4DC]/50 hover:text-[#C9A961] transition-colors">About Us</Link>
              <Link to="/rooms" className="block text-sm text-[#E8E4DC]/50 hover:text-[#C9A961] transition-colors">Rooms</Link>
              <Link to="/offers" className="block text-sm text-[#E8E4DC]/50 hover:text-[#C9A961] transition-colors">Offers</Link>
              <Link to="/events" className="block text-sm text-[#E8E4DC]/50 hover:text-[#C9A961] transition-colors">Events</Link>
              <Link to="/discover-bath" className="block text-sm text-[#E8E4DC]/50 hover:text-[#C9A961] transition-colors">Discover Bath</Link>
            </div>
          </div>
          <div>
            <h4 className="text-xs tracking-[0.15em] uppercase text-[#C9A961] mb-4 font-semibold">Legal</h4>
            <div className="space-y-2">
              <Link to="/privacy-policy" className="block text-sm text-[#E8E4DC]/50 hover:text-[#C9A961] transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="block text-sm text-[#E8E4DC]/50 hover:text-[#C9A961] transition-colors">Terms & Conditions</Link>
              <Link to="/cookie-policy" className="block text-sm text-[#E8E4DC]/50 hover:text-[#C9A961] transition-colors">Cookie Policy</Link>
            </div>
          </div>
          <div>
            <h4 className="text-xs tracking-[0.15em] uppercase text-[#C9A961] mb-4 font-semibold">Contact</h4>
            <div className="space-y-2 text-sm text-[#E8E4DC]/50">
              <p>106 Bloomfield Road</p>
              <p>Bath, BA2 2AP</p>
              <p>{settings.phone || '01225 339193'}</p>
              <p>{settings.email || 'stay@wentworthhouse.co.uk'}</p>
            </div>
          </div>
        </div>
        <div className="border-t border-[#C9A961]/10 pt-6 text-center text-xs text-[#E8E4DC]/30">
          &copy; {new Date().getFullYear()} Wentworth House Hotel. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

export function FooterCTA() {
  return (
    <section className="py-14 sm:py-18 px-4 sm:px-6 bg-gradient-to-b from-[#0F1419] to-[#1a1f2e] text-center">
      <div className="max-w-2xl mx-auto">
        <h2 className="text-2xl sm:text-3xl font-serif text-[#E8E4DC] mb-4">Ready to <span className="text-[#C9A961] italic">Book Your Stay?</span></h2>
        <p className="text-sm text-[#E8E4DC]/60 mb-8">Free parking, full English breakfast, and a peaceful base just minutes from Bath city centre.</p>
        <BookingDatePicker />
      </div>
    </section>
  );
}

export function MobileBookBar() {
  const bookingUrl = `${DIRECT_BOOKING_URL}?locale=en&currency=GBP&trackPage=no`;
  return (
    <div className="sm:hidden fixed bottom-0 left-0 right-0 z-50 p-3 bg-[#0F1419]/95 backdrop-blur-xl border-t border-[#C9A961]/20" data-testid="sticky-mobile-book">
      <a href={bookingUrl} target="_blank" rel="noopener noreferrer external" className="w-full bg-[#C9A961] text-[#0F1419] py-3.5 text-xs tracking-[0.2em] uppercase font-bold flex items-center justify-center gap-2 hover:bg-[#D4B76E] transition-all duration-300">
        <Calendar className="w-4 h-4" strokeWidth={2} /><span>Book Now</span>
      </a>
    </div>
  );
}
